/**
 * Sección "Mis empresas" — lista todas las orgs en las que el usuario
 * tiene Membership active. Marca cuál es la activa de la sesión y
 * permite cambiar a otra (mismo handler que el OrgSwitcher del header).
 */

import { Building2, Check, ExternalLink, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  ActionButton,
  InlineAlert,
  SettingsCard,
  SettingsSection,
} from '@/components/ui/settings-primitives'
import { useCurrentUser } from '@/hooks/api/use-current-user'
import { useSwitchOrg } from '@/hooks/use-switch-org'

export function OrganizationsSection() {
  const { data: me, isLoading } = useCurrentUser()
  const switchOrg = useSwitchOrg()
  const [busy, setBusy] = useState<string | null>(null)

  if (isLoading || !me) {
    return (
      <SettingsCard className="p-6">
        <div className="h-32 animate-pulse rounded bg-bg-elevated" />
      </SettingsCard>
    )
  }

  const memberships = me.memberships ?? []

  const onSwitch = async (org_id: string, label: string) => {
    setBusy(org_id)
    try {
      await switchOrg(org_id)
      toast.success(`Cambiaste a ${label}`)
      window.location.reload()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos cambiar de empresa')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Mis empresas"
        description={
          memberships.length === 0
            ? 'Aún no estás vinculado a ninguna empresa. Las empresas se crean al registrarse o por invitación.'
            : `Estás vinculado a ${memberships.length} empresa${
                memberships.length === 1 ? '' : 's'
              }. Puedes cambiar entre ellas en cualquier momento desde el header del panel o desde aquí.`
        }
      >
        {memberships.length === 0 ? (
          <InlineAlert variant="info">
            Si quieres crear una empresa, escríbenos a{' '}
            <strong>contacto@taxor.co</strong> para coordinar la asociación.
          </InlineAlert>
        ) : (
          <ul className="divide-y divide-border">
            {memberships.map((m) => {
              const org = m.organization
              if (!org) return null
              const isActive = m.is_active_session_org
              return (
                <li
                  key={m.membership_id}
                  className="flex items-start gap-3 py-4 first:pt-0 last:pb-0"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-bg-app text-fg-muted">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <p className="text-sm font-semibold text-fg-primary">
                        {org.razon_social}
                      </p>
                      {org.nombre_comercial && (
                        <span className="text-xs text-fg-muted">
                          ({org.nombre_comercial})
                        </span>
                      )}
                      {isActive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success-tint px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success-fg">
                          <Check className="h-3 w-3" />
                          Empresa actual
                        </span>
                      )}
                      {!org.is_active && (
                        <span className="rounded-full bg-warning-tint px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-fg">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-fg-muted">
                      {org.entity_type === 'company' ? 'NIT' : 'Doc'} {org.tax_id}
                      <span className="px-1">·</span>
                      <span className="font-mono uppercase tracking-wider text-fg-secondary">
                        {m.role}
                      </span>
                    </p>
                  </div>
                  {!isActive && (
                    <ActionButton
                      variant="secondary"
                      onClick={() => onSwitch(m.organization_id, org.razon_social)}
                      disabled={busy !== null}
                      loading={busy === m.organization_id}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Cambiar a esta
                    </ActionButton>
                  )}
                  {isActive && busy === m.organization_id && (
                    <Loader2 className="h-4 w-4 animate-spin text-fg-muted" />
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </SettingsSection>

      {me.active_org && (
        <SettingsSection
          title="Datos de la empresa actual"
          description="Información de la empresa con la que estás operando ahora mismo."
        >
          <ul className="divide-y divide-border text-sm">
            <li className="grid grid-cols-[140px,1fr] gap-2 py-2.5 first:pt-0">
              <span className="text-fg-muted">Razón social</span>
              <span className="font-medium text-fg-primary">
                {me.active_org.razon_social}
              </span>
            </li>
            {me.active_org.nombre_comercial && (
              <li className="grid grid-cols-[140px,1fr] gap-2 py-2.5">
                <span className="text-fg-muted">Nombre comercial</span>
                <span className="text-fg-primary">{me.active_org.nombre_comercial}</span>
              </li>
            )}
            <li className="grid grid-cols-[140px,1fr] gap-2 py-2.5">
              <span className="text-fg-muted">NIT</span>
              <span className="font-mono text-fg-primary">{me.active_org.tax_id}</span>
            </li>
            <li className="grid grid-cols-[140px,1fr] gap-2 py-2.5">
              <span className="text-fg-muted">Tipo</span>
              <span className="capitalize text-fg-primary">{me.active_org.entity_type}</span>
            </li>
            <li className="grid grid-cols-[140px,1fr] gap-2 py-2.5 last:pb-0">
              <span className="text-fg-muted">Tu rol</span>
              <span className="font-mono uppercase tracking-wider text-fg-primary">
                {me.active_org.role}
              </span>
            </li>
          </ul>
          <div className="mt-4">
            <InlineAlert variant="info">
              Para editar los datos de la empresa (razón social, dirección, etc.), tu
              rol debe ser <strong>OWNER</strong> o <strong>ADMIN</strong>. Esa edición
              vivirá pronto en una sección de "Administración de empresa" propia.
            </InlineAlert>
          </div>
        </SettingsSection>
      )}
    </div>
  )
}
